import { request } from "./rcps/api";
import type { Inputs, SavedEvaluation } from "./rcps/model";
import type { PeerSelectResult, WaccComputeBody, WaccResult } from "../types/dcf";

export const WACC_FORM_FIELDS = ["targetName", "valuationDate", "method", "bizText", "topk",
  "rfTenor", "rfCustom", "kdRating", "kdTenor", "kdCustom", "mrp", "size", "sizeCustom",
  "tax2", "betaFreq", "betaWin", "agg"] as const;
export type WaccForm = Record<typeof WACC_FORM_FIELDS[number], string> & {
  peerPicks: { symbol: string; name: string }[];
  peerResult: PeerSelectResult | null;
};
export type WaccEvaluation = {
  version: 1;
  kind: "wacc";
  inputs: Inputs;
  form: WaccForm;
  payload: WaccComputeBody;
  result: WaccResult;
  calculated: string;
};
export function waccSnapshot(form: WaccForm, payload: WaccComputeBody, result: WaccResult): WaccEvaluation {
  return structuredClone({ version: 1, kind: "wacc", form, payload, result,
    inputs: {
      params: { valuation_company: form.targetName, valuation_date: form.valuationDate },
      peers: form.peerPicks, rf: { periods: [], rates: [] }, kd: { periods: [], rates: [] },
    }, calculated: new Date().toISOString() });
}
export function validWaccEvaluation(value: unknown): value is WaccEvaluation {
  const e = value as WaccEvaluation | null;
  return !!(e?.version === 1 && e.kind === "wacc" && e.form &&
    WACC_FORM_FIELDS.every(key => typeof e.form[key] === "string") &&
    ["auto", "direct"].includes(e.form.method) &&
    Array.isArray(e.form.peerPicks) && e.form.peerPicks.every(p =>
      p && typeof p.symbol === "string" && typeof p.name === "string") &&
    e.inputs?.params && typeof e.inputs.params.valuation_date === "string" &&
    e.payload && typeof e.payload.peer_input_method === "string" &&
    typeof e.calculated === "string" && Number.isFinite(Date.parse(e.calculated)) &&
    typeof e.result?.wacc?.value === "number" && Number.isFinite(e.result.wacc.value));
}
export function saveWaccEvaluation(evaluation: WaccEvaluation, id: string): Promise<SavedEvaluation<WaccEvaluation>> {
  if (!validWaccEvaluation(evaluation)) throw new Error("저장할 WACC 계산 결과가 올바르지 않습니다.");
  return request(`/agent/user-docs/rcps/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify({ name: evaluation.form.targetName.trim() || `WACC · ${evaluation.form.valuationDate}`,
      summary: "wacc", payload: evaluation }),
  });
}
